import { useState, useEffect, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"
import { billingService } from "@/services/billing"

/**
 * Onboarding step inserted between password setup and organization creation.
 *
 * Flow:
 *  1. User lands here after /get-started/profile.
 *  2. We POST /api/billing/checkout-session to start a Stripe Checkout session
 *     (subscription mode, lowest tier, 7-day trial).
 *  3. We redirect the browser to the Stripe-hosted URL.
 *  4. After Stripe Checkout, Stripe redirects back to /get-started/organization
 *     with ?session_id=cs_... — that page reads the session, retrieves the
 *     resulting customer/subscription IDs, and submits them with the
 *     organization-creation request.
 *
 * If the user cancels on the Stripe page, Stripe returns to this page with
 * ?cancelled=true; we surface a toast and let them retry.
 */
export default function PaymentSetupPage() {
  const navigate = useNavigate()
  const [isStarting, setIsStarting] = useState(false)
  const startedRef = useRef(false)

  useEffect(() => {
    // Auth gate: must have an authToken from the prior step. If not, kick
    // them back to the start of the funnel.
    const authToken = localStorage.getItem('authToken')
    if (!authToken) {
      console.warn('payment-setup: no authToken; redirecting to home')
      navigate('/')
      return
    }

    // Stripe redirected back with ?cancelled=true
    const params = new URLSearchParams(window.location.search)
    if (params.get('cancelled') === 'true') {
      toast.error('Payment setup was cancelled. You can try again when you are ready.')
      // Strip the query so a refresh doesn't re-fire the toast
      window.history.replaceState({}, '', '/get-started/payment')
    }
  }, [navigate])

  const handleStartCheckout = async () => {
    if (startedRef.current) return
    startedRef.current = true
    setIsStarting(true)

    try {
      // Stripe substitutes {CHECKOUT_SESSION_ID} into the success_url query
      // string; the org-setup page reads it and passes it to POST /organizations.
      const { url } = await billingService.createCheckoutSession({
        success_path: '/get-started/organization',
        cancel_path: '/get-started/payment?cancelled=true',
      })

      if (!url) {
        throw new Error('No checkout URL returned by the billing service')
      }

      // Hard navigation — leave the SPA so Stripe Checkout can take over.
      window.location.href = url
    } catch (error: any) {
      console.error('Failed to start Stripe Checkout:', error)
      const detail = error?.response?.data?.detail
      toast.error(detail || error?.message || 'Could not start payment setup. Please try again.')
      setIsStarting(false)
      startedRef.current = false
    }
  }

  return (
    <div className="container relative h-screen flex-col items-center justify-center md:grid lg:max-w-none lg:grid-cols-2 lg:px-0">
      {/* Left brand panel — identical layout to organization-setup so the
          three onboarding steps feel like one continuous flow. */}
      <div className="relative hidden h-full flex-col bg-muted p-10 text-white lg:flex dark:border-r">
        <div className="absolute inset-0 bg-slate-700" />
        <div className="relative z-20 flex items-center text-lg font-medium">
          <img src="/logo_white.png" alt="Glaium Logo" className="h-8 w-auto" />
        </div>
      </div>

      <div className="flex h-full items-center p-4 lg:p-8 bg-muted/40">
        <div className="mx-auto flex w-full flex-col justify-center space-y-6 sm:w-[420px]">
          <div className="flex flex-col space-y-2 text-center">
            <h1 className="text-2xl font-semibold tracking-tight">Add a payment method</h1>
            <p className="text-sm text-muted-foreground">
              Start your 7-day free trial. We won't charge your card until the trial ends —
              cancel anytime from Settings.
            </p>
          </div>

          <div className="grid gap-4 rounded-lg border bg-background p-6">
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Full access to all features during the trial.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>No charge for 7 days — cancel before day 8 and you won't be billed.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-emerald-600">✓</span>
                <span>Card details are entered directly with Stripe; we never see them.</span>
              </div>
            </div>
            <Button
              onClick={handleStartCheckout}
              disabled={isStarting}
              className="w-full"
            >
              {isStarting ? 'Redirecting to Stripe…' : 'Continue to payment'}
            </Button>
            <p className="text-xs text-center text-muted-foreground">
              You'll be redirected to Stripe to securely enter your card details.
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
